import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  ViewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { User } from '@core/interfaces/user.interface';
import { autoMovePlugin, resizePlugin } from '@core/utils/slider.utils';
import { NgParticlesService, NgxParticlesModule } from '@tsparticles/angular';
import { Engine, ISourceOptions } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';
import { SvgIconComponent } from 'angular-svg-icon';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { ToastrService } from 'ngx-toastr';
import { CaptchaOverlayComponent } from '@core/components/captcha-overlay/captcha-overlay.component';
import { OverlayModule } from '@angular/cdk/overlay';
import { ModalService } from '@features/modals/services/modal.service';
import { RainService } from '@features/rain/rain.service';
import { fadeInAnimation } from '@core/utils/animations.util';
import { sleep } from '@core/utils/sleep.util';

@Component({
  selector: 'app-home-slides',
  imports: [
    SvgIconComponent,
    NgxParticlesModule,
    RouterLink,
    CaptchaOverlayComponent,
    OverlayModule,
  ],
  templateUrl: './home-slides.component.html',
  styleUrl: './home-slides.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    class: 'black-third-wrapper rounded-6 w-full h-full',
  },
  animations: [fadeInAnimation],
})
export class HomeSlidesComponent {
  ngParticlesService = inject(NgParticlesService);

  user = input.required<User | null>();

  @ViewChild('sliderRef') sliderRef!: ElementRef<HTMLElement>;
  slider: KeenSliderInstance | null = null;

  particlesOptions: ISourceOptions = {
    fullScreen: { enable: false },
    particles: {
      number: {
        value: 20,
      },
      color: {
        value: ['#ffffff'],
      },
      move: {
        direction: 'none',
        enable: true,
        outModes: {
          default: 'out',
        },
        random: true,
        speed: 1.25,
        straight: false,
      },
      opacity: {
        value: { min: 0.15, max: 0.69 },
      },
      size: {
        value: { min: 1, max: 1.2 },
      },
      wobble: {
        enable: false,
      },
    },
  };

  isCaptchaOverlay = false;

  constructor(
    private toastrService: ToastrService,
    private modalService: ModalService,
    public rainService: RainService
  ) {
    const initRainEffect = afterRenderEffect(async () => {
      if (this.rainService.rain().state < 3) {
        initRainEffect.destroy();
        await sleep(100);
        this.slider?.moveToIdx(1);
      }
    });
  }

  ngOnInit() {
    this.ngParticlesService.init(async (engine: Engine) => {
      await loadSlim(engine);
    });
  }

  ngAfterViewInit() {
    if (this.sliderRef?.nativeElement) {
      this.slider = new KeenSlider(
        this.sliderRef.nativeElement,
        {
          loop: true,
          slides: {
            perView: 1,
          },
        },
        [resizePlugin, autoMovePlugin]
      );
    }
  }

  ngOnDestroy() {
    if (this.slider) {
      this.slider.destroy();
    }
  }

  async handleVerifyCaptcha(captcha: string) {
    const message = await this.rainService.claimRain(captcha);
    this.toastrService.success(message);
    this.isCaptchaOverlay = false;
  }

  openOverlay() {
    if (!this.rainService.rain().captcha) {
      this.toastrService.error('Captcha loading error');
      return;
    }

    this.isCaptchaOverlay = true;
  }

  onPrevSlide() {
    this.slider?.prev();
  }

  onNextSlide() {
    this.slider?.next();
  }

  openTipRainModal() {
    this.modalService.openModal('TipRainModal');
  }
}
